import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Form,
  InputGroup,
  Modal,
  Spinner,
} from "react-bootstrap";
import { TrailDifficulty, TrailType, CarType } from "../../Data/Trails";
import { RouteStatus, RouteState } from "../../Data/Routes";
import {
  generateKML,
  parseKMLAndGetCoordinates,
} from "../../../Utilities/Helpers";
import * as _ from "lodash";
import "./Route.css";
import MultiPhotoUpload from "../Trail/components/multiPhotoUpload";
import RouteMapPreview from "./RouteMapPreview";

import { useRoute } from "../../../components/Route/hooks/useRoute";
import { routeService } from "../../../components/Route/services/routeService";
import TrailFormSection from "../Trail/components/TrailFormSection";
import { showError, showSuccess, showWarning } from "../../../Utilities/sweetAlert";
import TagMultiSelect from "../../../components/Tag/TagMultiSelect/TagMultiSelect";
import { Tag } from "../../../Resources/Data/Tags";

const RoutePage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    route,
    setRoute,
    originalRoute,
    setOriginalRoute,
    loading,
    setLoading,
    setNewRouteValue,
  } = useRoute(id);

  const isDraft = route.state === RouteState.Draft;

  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
  const [fileName, setFileName] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  function handleUpload() {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".kml";
    fileInput.onchange = (e: any) => {
      const file = e.target.files ? e.target.files[0] : null;
      if (file) {
        if (file.name.endsWith(".kml")) {
          setFileName(file.name);
          const reader = new FileReader();
          reader.onload = (event: ProgressEvent<FileReader>) => {
            const content = event.target?.result as string;
            const parsedCoords = parseKMLAndGetCoordinates(content);
            setRoute((prevRoute) => ({
              ...prevRoute,
              trailPath: parsedCoords,
            }));
          };
          reader.readAsText(file);
        } else {
          showWarning("Invalid File", "Please upload a file with a .kml extension.");
        }
      }
    };
    fileInput.click();
  }

  function handleDownload() {
    if (!route.trailPath || !route.trailPath.length) {
      showWarning("No Route Path", "No route path available to download.");
      return;
    }
    const kmlContent = generateKML(route.trailPath);
    const blob = new Blob([kmlContent], {
      type: "application/vnd.google-earth.kml+xml",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = route.name ? `${route.name}.kml` : "route.kml";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const handlePhotosChange = (files: File[]) => {
    setUploadedPhotos(files);
  };

  const handlePhotoDelete = (photoUrl: string) => {
    const photoKey = photoUrl.split("/").pop();

    if (photoKey) {
      routeService
        .deleteRoutePhoto(route.id.toString(), photoKey)
        .then(() => {
          showSuccess("Photo Deleted", "Photo deleted successfully");
          setRoute((prevRoute) => ({
            ...prevRoute,
            photos: prevRoute.photos?.filter((photo) => photo !== photoUrl),
          }));
        })
        .catch((error) => {
          console.error("Error deleting photo:", error);
          showError("Deletion Failed", "Failed to delete photo");
        });
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const response = await routeService.activateRoute(route.id);
      if (response.status === 200) {
        showSuccess('Route Approved', 'Route has been activated successfully!');
        setRoute(prev => ({ ...prev, status: RouteStatus.Active }));
        if (originalRoute) {
          setOriginalRoute({ ...originalRoute, status: RouteStatus.Active });
        }
      }
    } catch (error) {
      console.error('Error approving route:', error);
      showError('Approval Failed', 'Failed to approve route.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      const response = await routeService.deactivateRoute(route.id, rejectionReason || undefined);
      if (response.status === 200) {
        showSuccess('Route Rejected', 'Route has been deactivated.');
        setShowRejectModal(false);
        setRejectionReason('');
        setRoute(prev => ({ ...prev, status: RouteStatus.Inactive }));
        if (originalRoute) {
          setOriginalRoute({ ...originalRoute, status: RouteStatus.Inactive });
        }
      }
    } catch (error) {
      console.error('Error rejecting route:', error);
      showError('Rejection Failed', 'Failed to reject route.');
    } finally {
      setActionLoading(false);
    }
  };

  const updateRoute = () => {
    const delta = Object.keys(route!).reduce((acc, key) => {
      if (route![key] !== originalRoute![key]) {
        acc[key] = route![key];
      }
      return acc;
    }, {} as Record<string, any>);

    if (_.isEmpty(delta) && uploadedPhotos.length === 0) {
      showWarning("No Changes", "No changes detected.");
      return;
    }
    setLoading(true);

    if (uploadedPhotos.length > 0) {
      const formData = new FormData();
      uploadedPhotos.forEach(file => {
        formData.append('photos', file, file.name);
      });
      routeService.updateRoutePhotos(originalRoute!.id.toString(), formData).then(() => {
        showSuccess('Photos Updated', 'Route photos updated successfully!');
        setUploadedPhotos([]);

        return routeService
          .getRoute(id!)
          .then((response) => {
            setRoute(response.data.route);
            setOriginalRoute(response.data.route);
          })
          .catch((err) => {
            showError("Loading Error", "Error loading route data");
            console.error(err);
          });
      });
      delete delta.photos;
    }

    if (_.isEmpty(delta)) {
      setLoading(false);
      return;
    }

    // Handle tags - convert to tagIds array for backend
    if (delta.tags) {
      delta.tagIds = delta.tags.map((tag: any) => tag.id);
      delete delta.tags;
    }

    // Remove read-only fields from delta
    delete delta.creator;
    delete delta.trailPath;
    delete delta.startingPoint;
    delete delta.autoDetectedCountry;
    delete delta.autoDetectedCity;
    delete delta.reference;
    delete delta.createdBy;
    delete delta.ownerId;
    delete delta.updatedAt;
    delete delta.createdAt;

    routeService.updateRoute(originalRoute!.id.toString(), delta).then((response) => {
      if (response.status === 200 || response.status === 201) {
        showSuccess('Route Updated', 'Route edited successfully!');

        routeService.getRoute(id!).then((response) => {
          setUploadedPhotos([]);
          setRoute(response.data.route);
          setOriginalRoute(response.data.route);
          setLoading(false);
        }).catch((reloadError) => {
          console.error('Error reloading route data:', reloadError);
          showWarning('Update Success', 'Route updated successfully, but failed to reload data. Please refresh the page.');
          setLoading(false);
        });
      } else {
        showWarning('Update Warning', 'Route update may not have been completed successfully.');
        setLoading(false);
      }
    }).catch((error) => {
      console.error('Error updating route:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
      showError("Update Failed", `Failed to update route: ${errorMessage}`);
      setLoading(false);
    });
  };

  return (
    <Container className="single-element-container">
      <div className="trail-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2>{isDraft ? 'View Route' : 'Edit Route'}: {route?.name || 'Loading...'}</h2>
            {route?.id !== 0 && (
              <p className="text-muted">Route ID: {route.id}</p>
            )}
          </div>
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/routes')}
            className="d-flex align-items-center gap-2"
          >
            <i className="fas fa-arrow-left"></i>
            Back to Routes
          </Button>
        </div>
      </div>

      {/* Draft notice */}
      {isDraft && (
        <div className="alert alert-secondary d-flex align-items-center mb-4" role="alert">
          <i className="fas fa-lock me-2"></i>
          This route is a draft and cannot be edited from the admin panel. Only the creator can modify draft routes.
        </div>
      )}

      {/* Approve/Reject buttons for pending routes */}
      {!isDraft && route.status === RouteStatus.Pending && (
        <div className="route-action-buttons">
          <Button
            variant="success"
            onClick={handleApprove}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner animation="border" size="sm" /> : (
              <><i className="fas fa-check me-2"></i>Approve Route</>
            )}
          </Button>
          <Button
            variant="danger"
            onClick={() => setShowRejectModal(true)}
            disabled={actionLoading}
          >
            <i className="fas fa-times me-2"></i>Reject Route
          </Button>
        </div>
      )}

      <div>
        <TrailFormSection
          title="Photos"
          icon="fas fa-camera"
          className="photo-section"
        >
          {isDraft ? (
            <div className="photos-gallery">
              {(route?.photos || []).length > 0 ? (
                (route.photos).map((photoUrl, index) => (
                  <div key={photoUrl} className="photo-item">
                    <img src={photoUrl} alt={`Route photo ${index + 1}`} className="preview-image" />
                  </div>
                ))
              ) : (
                <p className="text-muted mb-0">No photos</p>
              )}
            </div>
          ) : (
            <MultiPhotoUpload
              onPhotosChange={handlePhotosChange}
              onPhotoDelete={handlePhotoDelete}
              initialPhotos={route?.photos || []}
            />
          )}
        </TrailFormSection>

        <TrailFormSection
          title="Route Path Preview"
          icon="fas fa-map"
          className="mb-4"
        >
          <RouteMapPreview coordinates={route?.trailPath || []} height="400px" />
        </TrailFormSection>

        <div className="trail-form-sections">
          {/* Read-only metadata */}
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">ID</InputGroup.Text>
            <Form.Control value={route?.id} disabled />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Created At</InputGroup.Text>
            <Form.Control value={route?.createdAt as unknown as string} disabled />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Creator</InputGroup.Text>
            <Form.Control
              value={route?.creator ? `${route.creator.name} (${route.creator.email})` : 'Unknown'}
              disabled
            />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">State</InputGroup.Text>
            <Form.Control value={route?.state || ''} disabled />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Status</InputGroup.Text>
            <Form.Control value={route?.status || ''} disabled />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Rating</InputGroup.Text>
            <Form.Control value={route?.rating} disabled />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Auto-detected Location</InputGroup.Text>
            <Form.Control
              value={`${route?.autoDetectedCity || 'N/A'}, ${route?.autoDetectedCountry || 'N/A'}`}
              disabled
            />
          </InputGroup>

          {/* Route Path */}
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Route Path</InputGroup.Text>
            <InputGroup.Text>.kml file only</InputGroup.Text>
            <Form.Control
              placeholder="No file chosen"
              value={fileName}
              disabled
            />
            {!isDraft && (
              <Button variant="outline-secondary" onClick={handleUpload}>
                Upload
              </Button>
            )}
            <Button variant="outline-secondary" onClick={handleDownload}>
              Download
            </Button>
          </InputGroup>

          {/* Editable fields (read-only for drafts) */}
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Route Name</InputGroup.Text>
            <Form.Control
              onChange={(e) => setNewRouteValue("name", e.target.value)}
              value={route?.name || ""}
              disabled={isDraft}
            />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Distance</InputGroup.Text>
            <Form.Control
              disabled
              value={route?.distance ? `${route.distance} km` : "Not specified"}
            />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Elevation Gain</InputGroup.Text>
            <Form.Control
              disabled
              value={route?.elevationGain ? `${route.elevationGain} m` : "Not specified"}
            />
          </InputGroup>
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Difficulty</InputGroup.Text>
            <Form.Select
              onChange={(e) =>
                setNewRouteValue("difficulty", e.target.value as TrailDifficulty)
              }
              value={route?.difficulty || ""}
              disabled={isDraft}
            >
              <option value="">Select difficulty</option>
              {Object.values(TrailDifficulty).map((difficulty) => (
                <option key={difficulty} value={difficulty}>
                  {difficulty}
                </option>
              ))}
            </Form.Select>
          </InputGroup>

          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Trail Type</InputGroup.Text>
            <Form.Select
              onChange={(e) =>
                setNewRouteValue("type", e.target.value as TrailType)
              }
              value={route?.type || ""}
              disabled={isDraft}
            >
              <option value="">Select trail type</option>
              {Object.values(TrailType).map((type) => (
                <option key={type} value={type}>
                  {type.replace('_', ' ')}
                </option>
              ))}
            </Form.Select>
          </InputGroup>

          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Car Type Required</InputGroup.Text>
            <Form.Select
              onChange={(e) =>
                setNewRouteValue("carType", e.target.value as CarType)
              }
              value={route?.carType || ""}
              disabled={isDraft}
            >
              <option value="">Select car type</option>
              {Object.values(CarType).map((carType) => (
                <option key={carType} value={carType}>
                  {carType === '_4x4' ? '4x4' : carType}
                </option>
              ))}
            </Form.Select>
          </InputGroup>

          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text className="meta-element-label">Description</InputGroup.Text>
            <Form.Control
              as="textarea"
              onChange={(e) => setNewRouteValue("description", e.target.value)}
              value={route?.description || ""}
              disabled={isDraft}
            />
          </InputGroup>

          {/* Tags Section */}
          {isDraft ? (
            <InputGroup size="sm" className="mb-3">
              <InputGroup.Text className="meta-element-label">Tags</InputGroup.Text>
              <Form.Control
                value={route?.tags?.map(tag => tag.name).join(', ') || 'None'}
                disabled
              />
            </InputGroup>
          ) : (
            <TagMultiSelect
              selectedTags={route?.tags?.map(tag => ({ id: tag.id, name: tag.name })) || []}
              onTagsChange={(tags: Tag[]) => {
                setNewRouteValue("tags", tags.map(tag => ({ id: tag.id, name: tag.name })));
              }}
              placeholder="Search and select tags for this route..."
              allowCreate={true}
              maxTags={10}
              className="mb-3"
            />
          )}

          {!isDraft && (
            <div className="trail-buttons">
              <Button variant="success" onClick={updateRoute} disabled={loading}>
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Update"
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Loading Modal */}
      <Modal show={loading} backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Processing your request, please wait...</p>
        </Modal.Body>
      </Modal>

      {/* Rejection Reason Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reject Route</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to reject <strong>{route?.name || 'this route'}</strong>?</p>
          <Form.Group>
            <Form.Label>Rejection Reason (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter a reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={actionLoading}>
            {actionLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              'Reject Route'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RoutePage;
